/**
 * Alarms API hooks — full CRUD + toggle + channel linking + playbook.
 * Alarm = alert rule on a watchpoint: conditions + severity + channels.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useToast } from '@vnetwork/ui/feedback';
import {
  healthcheckApi,
  hcKeys,
  hcExtractError,
  unwrap,
  unwrapList,
  type Alarm,
  type AlarmSeverity,
  type Channel,
} from './_shared';

// ---- Input types ---------------------------------------------------------

export interface CreateAlarmInput {
  watchpoint_id: string;
  name: string;
  severity?: AlarmSeverity;
  /** Raw conditions JSON — use condition-serializer to build the right shape. */
  conditions: unknown;
  consecutive_failures?: number;
  cooldown_minutes?: number;
  playbook_id?: string | null;
  channel_ids?: string[];
  /** Send the "Recovered" notification when this alarm clears (omit → backend defaults true). */
  notify_on_recovery?: boolean;
}

/** Same body but without watchpoint_id (scoped by URL). */
export type CreateWatchpointAlarmInput = Omit<CreateAlarmInput, 'watchpoint_id'>;

export interface UpdateAlarmInput {
  name?: string;
  severity?: AlarmSeverity;
  conditions?: unknown;
  consecutive_failures?: number;
  cooldown_minutes?: number;
  playbook_id?: string | null;
  is_active?: boolean;
  /** Toggle the "Recovered" notification for this alarm. */
  notify_on_recovery?: boolean;
}

// ---- Alarm queries -------------------------------------------------------

export function useAlarms(q?: Record<string, unknown>) {
  return useQuery<Alarm[]>({
    queryKey: hcKeys.alarms(q),
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<Alarm>(await healthcheckApi.get('/alarms', { params: q })).items,
  });
}

export function useAlarm(id: string | undefined) {
  return useQuery<Alarm>({
    queryKey: hcKeys.alarm(id ?? ''),
    enabled: !!id,
    staleTime: 15_000,
    queryFn: async () => unwrap<Alarm>(await healthcheckApi.get(`/alarms/${id}`)),
  });
}

/** All alarms for a specific watchpoint (`GET /watchpoints/:id/alarms`). */
export function useWatchpointAlarms(watchpointId: string | undefined) {
  return useQuery<Alarm[]>({
    queryKey: hcKeys.watchpointAlarms(watchpointId ?? ''),
    enabled: !!watchpointId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<Alarm>(
        await healthcheckApi.get(`/watchpoints/${watchpointId}/alarms`),
      ).items,
  });
}

/** Channels linked to an alarm (`GET /alarms/:id/channels`). */
export function useAlarmChannels(alarmId: string | undefined) {
  return useQuery<Channel[]>({
    queryKey: [...hcKeys.alarm(alarmId ?? ''), 'channels'],
    enabled: !!alarmId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<Channel>(await healthcheckApi.get(`/alarms/${alarmId}/channels`)).items,
  });
}

// ---- Alarm mutations -----------------------------------------------------

export function useCreateAlarm(
  options?: UseMutationOptions<Alarm, Error, CreateAlarmInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Alarm, Error, CreateAlarmInput>({
    mutationFn: async (input) =>
      unwrap<Alarm>(await healthcheckApi.post('/alarms', input)),
    ...options,
    onSuccess: (...args) => {
      const alarm = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      qc.invalidateQueries({ queryKey: hcKeys.watchpointAlarms(alarm.watchpoint_id) });
      show({ tone: 'success', title: `Alarm "${alarm.name}" created` });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Create failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useCreateWatchpointAlarm(
  watchpointId: string,
  options?: UseMutationOptions<Alarm, Error, CreateWatchpointAlarmInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Alarm, Error, CreateWatchpointAlarmInput>({
    mutationFn: async (input) =>
      unwrap<Alarm>(
        await healthcheckApi.post(`/watchpoints/${watchpointId}/alarms`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      const alarm = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      qc.invalidateQueries({ queryKey: hcKeys.watchpointAlarms(watchpointId) });
      show({ tone: 'success', title: `Alarm "${alarm.name}" created` });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Create failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useUpdateAlarm(
  id: string,
  options?: UseMutationOptions<Alarm, Error, UpdateAlarmInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Alarm, Error, UpdateAlarmInput>({
    mutationFn: async (input) =>
      unwrap<Alarm>(await healthcheckApi.put(`/alarms/${id}`, input)),
    ...options,
    onSuccess: (...args) => {
      const alarm = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.alarm(id) });
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      qc.invalidateQueries({ queryKey: hcKeys.watchpointAlarms(alarm.watchpoint_id) });
      show({ tone: 'success', title: 'Alarm updated' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useDeleteAlarm(options?: UseMutationOptions<void, Error, string>) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.delete(`/alarms/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      show({ tone: 'success', title: 'Alarm deleted' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Delete failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

/** Toggle `is_active` on an alarm via PUT (partial update). */
export function useToggleAlarm(options?: UseMutationOptions<Alarm, Error, { id: string; is_active: boolean }>) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Alarm, Error, { id: string; is_active: boolean }>({
    mutationFn: async ({ id, is_active }) =>
      unwrap<Alarm>(await healthcheckApi.put(`/alarms/${id}`, { is_active })),
    ...options,
    onSuccess: (...args) => {
      const alarm = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.alarm(alarm.id) });
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      qc.invalidateQueries({ queryKey: hcKeys.watchpointAlarms(alarm.watchpoint_id) });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

// ---- Channel link/unlink -------------------------------------------------

export function useLinkAlarmChannel(
  alarmId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (channelId) => {
      await healthcheckApi.post(`/alarms/${alarmId}/channels`, { channel_id: channelId });
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: [...hcKeys.alarm(alarmId), 'channels'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUnlinkAlarmChannel(
  alarmId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (channelId) => {
      await healthcheckApi.delete(`/alarms/${alarmId}/channels/${channelId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: [...hcKeys.alarm(alarmId), 'channels'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface SetAlarmPlaybookVars {
  alarmId: string;
  playbookId: string | null;
}

/** PATCH /alarms/:id/playbook — set or detach (null) the linked playbook.
 *  Both alarmId and playbookId are passed at mutate() time to avoid stale-closure bugs.
 */
export function useSetAlarmPlaybook(
  options?: UseMutationOptions<void, Error, SetAlarmPlaybookVars>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, SetAlarmPlaybookVars>({
    mutationFn: async ({ alarmId, playbookId }) => {
      await healthcheckApi.patch(`/alarms/${alarmId}/playbook`, { playbook_id: playbookId });
    },
    ...options,
    onSuccess: (...args) => {
      const { alarmId } = args[1];
      qc.invalidateQueries({ queryKey: hcKeys.alarm(alarmId) });
      qc.invalidateQueries({ queryKey: hcKeys.alarms() });
      options?.onSuccess?.(...args);
    },
  });
}

export type { Alarm, AlarmSeverity };
